import { useState, useMemo, useEffect } from 'react';
// externals
import { useDispatch, useSelector } from 'react-redux';
// constants/enums
import { REGISTRATION_TYPE, STATE_REGISTRATION_TYPE_ALLOWLIST } from './constants';
import { SelectInput } from '../tradeForm/vehicle/TradeInSelectInput';
import type { DatePickerEventValue } from '@interstate/components/DatePicker';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { StyledDatePicker } from '../tradeForm/lienholderForm/LienholderForm.interstate.style';
import { dealLockHooks } from '@makemydeal/dr-dash-components';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as dealScreenErrorSelectors from '@makemydeal/dr-dash-store/selectors/dealScreenErrorSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

const RegistrationField = () => {
    const dispatch = useDispatch();
    const transferRegistrationDetails = useSelector(offerReduxSelectors.getTransferRegistrationDetails);
    const [showRegistrationDatePicker, setShowRegistrationDatePicker] = useState<boolean>(
        transferRegistrationDetails.registrationType === 'Transfer'
    );
    const [isDefaultExpiryDate, setIsDefaultExpiryDate] = useState<boolean>(false);

    const currentDate = useMemo(() => new Date(), []);

    const nextMonthFirstDate = useMemo(() => {
        const date = new Date();
        date.setMonth(date.getMonth() + 1);
        date.setDate(1);
        return date;
    }, []);

    const { registrationType, registrationExpiresOn } = transferRegistrationDetails;
    const initialRegistrationType = registrationType || 'New';
    const registrationExpiryDate = registrationExpiresOn ? new Date(registrationExpiresOn) : nextMonthFirstDate;
    const isLocked = dealLockHooks.useIsDealLocked();
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const [isDateBeforeMinDate, setIsDateBeforeMinDate] = useState<boolean>(false);
    const registrationTypeState = useSelector(
        dealScreenErrorSelectors.getFieldValidationError(DealTabs.FEES, 'registrationExpiryDate')
    );
    const hasDateValidationError = showRegistrationDatePicker && (isDefaultExpiryDate || isDateBeforeMinDate);

    const onRegistrationTypeChange = (event: any) => {
        const registrationType = event.target.value;
        dispatch(offerReduxActionCreators.setTransferRegistrationDetails({ registrationType }, registrationType !== 'Transfer'));
    };

    const onRegistrationExpiryDateChange = (event: InterstateOnChangeEvent<DatePickerEventValue>): void => {
        const registrationExpiresOn = event.target.value.dateValue;
        dispatch(
            offerReduxActionCreators.setTransferRegistrationDetails(
                { registrationType: initialRegistrationType, registrationExpiresOn },
                true
            )
        );
        setIsDefaultExpiryDate(false);
    };

    const onRegistrationExpiryDateBlur = (e: InterstateOnChangeEvent<DatePickerEventValue>) => {
        if (isDealScreenRedDotErrorsEnabled) {
            setIsDateBeforeMinDate(!e.isValid);
        }
    };

    useEffect(() => {
        setIsDefaultExpiryDate(registrationExpiryDate.getTime() === nextMonthFirstDate.getTime());
    }, [registrationExpiryDate]);

    useEffect(() => {
        setShowRegistrationDatePicker(initialRegistrationType === 'Transfer');
    }, [initialRegistrationType]);

    useEffect(() => {
        if (!isDealScreenRedDotErrorsEnabled) {
            return;
        }
        if (registrationTypeState !== hasDateValidationError) {
            dispatch(dealScreenErrorActionCreators.setFieldError(DealTabs.FEES, 'registrationExpiryDate', hasDateValidationError));
        }
    }, [dispatch, hasDateValidationError]);

    const customerState = useSelector(offerReduxSelectors.getCustomerState);
    const registrationTypeOptions = useMemo(() => {
        const buyerState = customerState?.toUpperCase?.() ?? '';
        const allowedValuesForState = STATE_REGISTRATION_TYPE_ALLOWLIST[buyerState];
        if (!allowedValuesForState) {
            return REGISTRATION_TYPE; // fallback when no rule exists
        }
        return REGISTRATION_TYPE.filter((option) => allowedValuesForState.includes(option.value));
    }, [customerState]);

    return (
        <>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <SelectInput
                    id="registration-type-select-input"
                    data-testid="registration-type-select-input"
                    label={'Registration Type'}
                    name={'Registration Type'}
                    onChange={onRegistrationTypeChange}
                    options={registrationTypeOptions}
                    value={initialRegistrationType}
                    placeholder="Select"
                    disabled={isLocked}
                    required
                    displayDeselectOption={false}
                    size={'sm'}
                />
                <div style={{ width: '35%', gap: '1rem' }}>
                    {showRegistrationDatePicker && (
                        <StyledDatePicker
                            id="registration-expiry-date-input"
                            data-testid="registration-expiry-date-input"
                            label={'Registration Expiration Date'}
                            onChange={onRegistrationExpiryDateChange}
                            onBlur={(e) => onRegistrationExpiryDateBlur(e)}
                            value={registrationExpiryDate}
                            minDate={currentDate}
                            errorMessage={'Modify the date for better accuracy.'}
                            hasError={isDefaultExpiryDate}
                            size={'sm'}
                            required
                            disabled={isLocked}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default RegistrationField;
